import {
  AtividadesCelula,
  Body,
  Button,
  Container,
  Header,
  ItemAtividade,
  ListaAtividades,
  PopoverAtividades,
  PrimeiraAtividade,
  TagAtividadeExtra,
  TextoHeader,
} from "./style";
import { Table } from "containers/Table";
import { IColum } from "containers/Table/interfaces";
import { utils, writeFileXLSX } from "xlsx";
import { ChangeEvent, useCallback } from "react";
import { FaEye, FaPen, FaPlus } from "react-icons/fa";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { IFornecedorPF, IFornecedorPJ } from "../../interfaces/IFornecedor";
import { StatusFornecedor } from "../StatusFornecedor";
import { SwitchCustom } from "components/Switch";
import usePermissoes from "hooks/usePermissoes";

const separarAtividades = (atividades?: string | null) =>
  atividades ? atividades.split("|") : [];

interface IAtividadesColunaCellProps {
  idFornecedor: number;
  atividades: string[];
}

function AtividadesColunaCell({
  idFornecedor,
  atividades,
}: IAtividadesColunaCellProps) {
  if (!atividades.length) {
    return <span>-</span>;
  }

  const [primeira] = atividades;
  const extras = atividades.length - 1;

  const popover = (
    <PopoverAtividades id={`popover-atividades-${idFornecedor}`}>
      <Popover.Body>
        <ListaAtividades>
          {atividades.map((atividade) => (
            <ItemAtividade key={atividade}>{atividade}</ItemAtividade>
          ))}
        </ListaAtividades>
      </Popover.Body>
    </PopoverAtividades>
  );

  return (
    <AtividadesCelula>
      <PrimeiraAtividade title={primeira}>{primeira}</PrimeiraAtividade>
      {extras > 0 && (
        <OverlayTrigger
          placement="top"
          trigger={["hover", "focus"]}
          overlay={popover}
        >
          <TagAtividadeExtra>+{extras}</TagAtividadeExtra>
        </OverlayTrigger>
      )}
    </AtividadesCelula>
  );
}

interface IProps {
  dados: (IFornecedorPJ | IFornecedorPF)[];
  loading: boolean;
  totalRows: number;
  onChangeRowsPerPage: (limit: number) => void;
  rowsPerPage: number;
  onChangePage: (page: number) => void;
  handleAtualizarFornecedor: (idFornecedor: number) => void;
  handleAtivacaoFornecedor: (idFornecedor: number, checked: boolean) => void;
  handleVisualizarFornecedor: (idFornecedor: number) => void;
  handleCadastrarFornecedor: () => void;
  acaoFornecedor: "VER" | "ATUALIZAR" | "LISTAR" | "CADASTRAR";
  buscarDadosPlanilha: () => Promise<(IFornecedorPJ | IFornecedorPF)[]>;
}

export function ListarFornecedores({
  dados,
  loading,
  totalRows,
  onChangeRowsPerPage,
  rowsPerPage,
  onChangePage,
  handleAtualizarFornecedor,
  handleAtivacaoFornecedor,
  handleVisualizarFornecedor,
  handleCadastrarFornecedor,
  buscarDadosPlanilha,
}: IProps) {
  const { temPermissao } = usePermissoes();
  const showBtnCadastrar = temPermissao("cadastrar_fornecedores");
  const showOptEditar = temPermissao("editar_fornecedores");
  const showOptAtivacao = temPermissao("excluir_fornecedores");

  const columns: IColum<IFornecedorPJ | IFornecedorPF>[] = [
    {
      name: <span>ID</span>,
      selector: (fornecedor: IFornecedorPJ | IFornecedorPF) => fornecedor.id,
      width: "90px",
      omit: true,
    },

    {
      name: <span>Nome do fornecedor</span>,
      selector: (fornecedor: IFornecedorPJ | IFornecedorPF) =>
        fornecedor.nomeRazaoSocial,
    },
    {
      name: <span>CPF/CNPJ</span>,
      selector: (fornecedor: IFornecedorPJ | IFornecedorPF) =>
        fornecedor.cpfCnpj,
    },
    {
      name: <span>Status</span>,
      selector: (fornecedor: IFornecedorPJ | IFornecedorPF) => (
        <StatusFornecedor status={fornecedor.status} />
      ),
    },
    {
      name: <span>Atividade principal</span>,
      selector: (fornecedor: IFornecedorPJ | IFornecedorPF) => (
        <AtividadesColunaCell
          idFornecedor={fornecedor.id}
          atividades={separarAtividades(fornecedor.atividadesPrincipais)}
        />
      ),
    },
    {
      name: <span>Opções</span>,
      selector: (fornecedor: IFornecedorPJ | IFornecedorPF) => (
        <span style={{ display: "flex", alignItems: "center" }}>
          <FaEye
            onClick={() => handleVisualizarFornecedor(fornecedor.id)}
            style={{ marginRight: "24px", cursor: "pointer" }}
            title="Visualizar"
          />
          {showOptEditar && (
            <FaPen
              onClick={() => handleAtualizarFornecedor(fornecedor.id)}
              style={{ marginRight: "24px", cursor: "pointer", color: "#000" }}
              title="Editar"
            />
          )}
          {showOptAtivacao && (
            <SwitchCustom
              checked={fornecedor.status === "ATIVO"}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                handleAtivacaoFornecedor(fornecedor.id, event.target.checked);
              }}
              title={fornecedor.status === "ATIVO" ? "Inativar" : "Ativar"}
            />
          )}
        </span>
      ),
      width: "140px",
    },
  ];

  const handleExport = useCallback(async () => {
    const todosFornecedores = await buscarDadosPlanilha();

    const dadosPlanilha = todosFornecedores.map(
      (item: IFornecedorPJ | IFornecedorPF) => ({
        ID: item.id,
        "Nome / Razão Social": item.nomeRazaoSocial,
        "CPF / CNPJ": item.cpfCnpj,
        "Atividade principal":
          separarAtividades(item.atividadesPrincipais).join(", ") || "-",
      })
    );

    const planilha = utils.json_to_sheet(dadosPlanilha);
    planilha["!cols"] = [{ wch: 5 }, { wch: 12 }, { wch: 10 }, { wch: 40 }];
    const workBook = utils.book_new();
    utils.book_append_sheet(workBook, planilha, "Fornecedores");
    writeFileXLSX(workBook, "lista_fornecedores.xlsx");
  }, [buscarDadosPlanilha]);

  return (
    <Container>
      <Header>
        <TextoHeader>Fornecedores cadastrados</TextoHeader>
        {showBtnCadastrar && (
          <Button onClick={handleCadastrarFornecedor}>
            <FaPlus /> Cadastrar fornecedor
          </Button>
        )}
      </Header>
      <hr style={{ marginTop: "1.5rem" }} />
      {dados.length ? (
        <Body>
          <Table
            keyField="id"
            totalRows={totalRows}
            pagination={true}
            columns={columns}
            data={dados}
            paginationRowsPerPageOptions={[10, 20, 50]}
            loading={loading}
            onChangeRowsPerPage={onChangeRowsPerPage}
            rowsPerPage={rowsPerPage}
            onChangePage={onChangePage}
            exportExcel={true}
            handleExport={handleExport}
            buttonExportVariant="secondary"
          />
        </Body>
      ) : (
        <div
          style={{
            width: "1370",
            marginTop: "60px",
            height: "85",
            top: "488px",
            left: "323px",
            gap: "10px",
            borderRadius: "5px",
            paddingTop: "32px",
            paddingBottom: "32px",
            backgroundColor: "#f5f5f5",
            textAlign: "center",
          }}
        >
          Não foram encontrados registros para sua busca.
        </div>
      )}
    </Container>
  );
}
